import { el, clear, fmtDate } from '../ui/dom';
import { renderShell, renderError, renderLoading } from '../ui/shell';
import { listAccounts } from '../api/accounts';
import type {
  AccountListResult,
  AdminAccountOrganization,
  AdminAccountRow,
  AdminAccountSubscription,
} from '../api/accounts';
import { UnauthorizedError } from '../api/client';
import { navigate } from '../router';
import type { RouteContext } from '../router';
import {
  SEARCH_MAX_LENGTH,
  SORT_OPTIONS,
  accountsListParams,
  accountsPath,
  applyPageClick,
  applySearchTerm,
  applySignInFilter,
  applySortOption,
  emptyMessage,
  exactTime,
  isPrivateRelay,
  lastSeenDisplay,
  lastSignInLabel,
  membershipIsActive,
  membershipLabel,
  parseAccountsQuery,
  productShortName,
  signInLabel,
  sortOptionValue,
  subscriptionStatusLabel,
  subscriptionTone,
  totalLabel,
} from './accounts-support';
import type { AccountsQueryState, SubscriptionTone } from './accounts-support';

const PAGE_SIZE = 50;
const SEARCH_DEBOUNCE_MS = 300;
const COLUMNS = ['Email', 'Name', 'Sign-in', 'Teams', 'App Store', 'Created', 'Last seen'];

// Every value below reaches the page through text nodes (el() children and
// textContent). Account emails, names and team names are user-supplied, so
// nothing here may build markup from them.
export async function renderAccounts({ root, query }: RouteContext): Promise<void> {
  const content = renderShell(root, 'accounts');

  // Seeded from the URL so a search is shareable and survives a reload.
  let state: AccountsQueryState = parseAccountsQuery(query);

  const searchInput = el('input', {
    class: 'control control-search',
    type: 'search',
    placeholder: 'Search email, name or account ID…',
    value: state.search,
    maxLength: SEARCH_MAX_LENGTH,
  });
  searchInput.setAttribute('aria-label', 'Search accounts');

  const signInSelect = selectControl(
    [
      ['', 'All sign-ins'],
      ['apple', 'Apple'],
      ['email', 'Email link'],
    ],
    state.signIn ?? '',
    'Filter by sign-in method',
  );
  const sortSelect = selectControl(
    SORT_OPTIONS.map((o) => [o.value, o.label]),
    sortOptionValue(state),
    'Sort accounts',
  );

  const results = el('div', { class: 'results' });
  content.append(
    el('div', { class: 'page-head' }, [el('h1', {}, ['Accounts'])]),
    el('p', { class: 'view-caption' }, [
      'Every Check ID account. Search by email, name, or account ID.',
    ]),
    el('div', { class: 'controls' }, [
      searchInput,
      el('div', { class: 'controls-right' }, [signInSelect, sortSelect]),
    ]),
    results,
  );

  // Typing fires a request per pause; a slow earlier response must not paint
  // over the newer one.
  let loadSeq = 0;

  async function load(): Promise<void> {
    // The debounce timer can outlive this view; do not rewrite another page's URL.
    if (!results.isConnected) return;
    const seq = ++loadSeq;
    renderLoading(results, 'Loading accounts…');
    history.replaceState({}, '', accountsPath(state));
    try {
      const data = await listAccounts(accountsListParams(state, PAGE_SIZE));
      if (seq !== loadSeq) return;
      if (data.items.length === 0 && data.total > 0 && state.page > data.totalPages) {
        // The list shrank under a bookmarked page; show the last one that exists.
        state = { ...state, page: Math.max(1, data.totalPages) };
        return load();
      }
      renderResults(results, data, state, (page) => {
        state = applyPageClick(state, withPendingSearch(), page);
        window.scrollTo(0, 0);
        void load();
      });
    } catch (err) {
      if (seq !== loadSeq) return;
      if (err instanceof UnauthorizedError) return navigate('/login');
      renderError(results, err instanceof Error ? err.message : 'Failed to load accounts.');
    }
  }

  let debounce: number | undefined;

  // A search still waiting for its pause is folded in before another control
  // changes, so one request goes out with both, not one with the old term.
  // It also cancels the pause, so the timer cannot fire a second request.
  function withPendingSearch(): AccountsQueryState {
    window.clearTimeout(debounce);
    return applySearchTerm(state, searchInput.value) ?? state;
  }

  function applySearch(): void {
    window.clearTimeout(debounce);
    const next = applySearchTerm(state, searchInput.value);
    if (!next) return;
    state = next;
    void load();
  }

  searchInput.addEventListener('input', () => {
    window.clearTimeout(debounce);
    debounce = window.setTimeout(applySearch, SEARCH_DEBOUNCE_MS);
  });
  searchInput.addEventListener('keydown', (ev) => {
    if (ev.key === 'Enter') applySearch();
  });

  signInSelect.addEventListener('change', () => {
    state = applySignInFilter(withPendingSearch(), signInSelect.value);
    void load();
  });

  sortSelect.addEventListener('change', () => {
    state = applySortOption(withPendingSearch(), sortSelect.value);
    void load();
  });

  await load();
}

function selectControl(
  options: string[][],
  selected: string,
  label: string,
): HTMLSelectElement {
  const sel = el('select', { class: 'control' });
  sel.setAttribute('aria-label', label);
  for (const [value, text] of options) {
    const opt = el('option', { value, textContent: text });
    if (value === selected) opt.selected = true;
    sel.append(opt);
  }
  return sel;
}

function renderResults(
  container: HTMLElement,
  data: AccountListResult,
  state: AccountsQueryState,
  onPage: (page: number) => void,
): void {
  clear(container);

  const summary = el('p', {
    class: 'view-caption',
    style: 'margin:0 0 12px;',
    textContent: totalLabel(data.total, state),
  });
  if (data.items.length === 0) {
    container.append(summary, el('div', { class: 'empty' }, [emptyMessage(state)]));
    return;
  }

  container.append(
    summary,
    el('div', { class: 'table-wrap' }, [
      el('table', { class: 'data-table accounts-table' }, [
        el('thead', {}, [
          el('tr', {}, COLUMNS.map((h) => el('th', { textContent: h }))),
        ]),
        el('tbody', {}, data.items.map(accountRow)),
      ]),
    ]),
    pagination(data, onPage),
  );
}

function accountRow(account: AdminAccountRow): HTMLTableRowElement {
  const chevron = el('span', { class: 'row-chevron', textContent: '▸' });
  chevron.setAttribute('aria-hidden', 'true');

  const row = el(
    'tr',
    {
      class: 'row-clickable',
      tabIndex: 0,
      onClick: () => toggleDetail(row, account, chevron),
    },
    [
      el('td', { class: 'col-nowrap' }, [emailCell(account, chevron)]),
      el('td', {
        class: account.name ? '' : 'cell-muted',
        textContent: account.name || '—',
      }),
      el('td', {}, [signInCell(account)]),
      el('td', {}, [teamsList(account.organizations)]),
      el('td', {}, [subscriptionList(account.appleSubscriptions, 'compact')]),
      el('td', {
        class: 'cell-muted col-nowrap',
        textContent: fmtDate(account.createdAt),
        title: exactTime(account.createdAt),
      }),
      lastSeenCell(account),
    ],
  );
  row.setAttribute('aria-expanded', 'false');
  row.addEventListener('keydown', (ev) => {
    // Only the row itself: Enter on the Copy button or a team link is theirs.
    if (ev.target !== row || (ev.key !== 'Enter' && ev.key !== ' ')) return;
    ev.preventDefault();
    toggleDetail(row, account, chevron);
  });
  return row;
}

function emailCell(account: AdminAccountRow, chevron: HTMLElement): HTMLElement {
  const line = el('div', { class: 'cell-line cell-line-nowrap' }, [
    chevron,
    el('span', {
      class: account.email ? 'cell-strong' : 'cell-muted',
      textContent: account.email || '—',
    }),
    isPrivateRelay(account.email)
      ? el('span', {
          class: 'pill pill-info',
          textContent: 'Apple relay',
          title: 'Hide My Email address. It forwards to the person’s real inbox and is not their own email.',
        })
      : null,
  ]);

  const badges: HTMLElement[] = [];
  if (account.scheduledDeletionAt) {
    badges.push(
      el('span', {
        class: 'pill pill-danger',
        textContent: 'Deletion pending',
        title: `Scheduled for ${exactTime(account.scheduledDeletionAt)}`,
      }),
    );
  }
  if (account.isAdmin) {
    badges.push(el('span', { class: 'pill pill-other', textContent: 'Admin' }));
  }
  return el('div', {}, [line, badges.length > 0 ? el('div', { class: 'cell-badges' }, badges) : null]);
}

function signInCell(account: AdminAccountRow): HTMLElement {
  return el('div', { class: 'cell-line' }, [
    el('span', { textContent: signInLabel(account.signInMethod) }),
    account.hasPasskey
      ? el('span', {
          class: 'pill pill-info',
          textContent: 'passkey',
          title: 'Has a registered passkey',
        })
      : null,
  ]);
}

function lastSeenCell(account: AdminAccountRow): HTMLElement {
  const seen = lastSeenDisplay(account.lastSeenAt);
  return el('td', { class: 'cell-muted', textContent: seen.text, title: seen.tooltip });
}

function orgLink(org: AdminAccountOrganization): HTMLAnchorElement {
  const href = `/organizations/${encodeURIComponent(org.id)}`;
  return el('a', {
    class: membershipIsActive(org.status) ? 'account-link' : 'account-link account-link-muted',
    href,
    textContent: membershipLabel(org),
    onClick: (ev) => {
      // A link inside a clickable row: never also toggle the row.
      ev.stopPropagation();
      // Keep new-tab and new-window clicks native; navigate in-app otherwise.
      if (ev.button !== 0 || ev.metaKey || ev.ctrlKey || ev.shiftKey || ev.altKey) return;
      ev.preventDefault();
      navigate(href);
    },
  });
}

function teamsList(orgs: AdminAccountOrganization[]): HTMLElement {
  if (orgs.length === 0) return el('span', { class: 'cell-muted', textContent: '—' });
  return el('div', { class: 'cell-stack' }, orgs.map(orgLink));
}

function toneClass(tone: SubscriptionTone): string {
  switch (tone) {
    case 'ok':
      return 'pill pill-ok';
    case 'warn':
      return 'pill pill-warn';
    case 'danger':
      return 'pill pill-danger';
    case 'unknown':
      return 'pill pill-absent';
    default:
      return 'pill';
  }
}

function subscriptionList(
  subs: AdminAccountSubscription[],
  mode: 'compact' | 'full',
): HTMLElement {
  if (subs.length === 0) {
    return el('span', {
      class: 'cell-muted',
      textContent: mode === 'full' ? 'No App Store subscriptions linked.' : '—',
    });
  }
  return el(
    'div',
    { class: 'cell-stack' },
    subs.map((sub) => {
      const pill = el('span', {
        class: toneClass(subscriptionTone(sub.status)),
        textContent: subscriptionStatusLabel(sub.status),
        // Assigning title = undefined would render the word "undefined".
        ...(sub.expiresAt ? { title: `Expires ${exactTime(sub.expiresAt)}` } : {}),
      });
      if (mode === 'compact') {
        return el('div', { class: 'cell-line' }, [productShortName(sub.productId), pill]);
      }
      return el('div', { class: 'sub-detail' }, [
        el('div', { class: 'cell-line' }, [
          el('span', { class: 'cell-strong', textContent: productShortName(sub.productId) }),
          pill,
        ]),
        el('div', { class: 'mono cell-muted', textContent: sub.productId ?? 'no product ID' }),
        el('div', { class: 'cell-muted', textContent: `Expires: ${exactTime(sub.expiresAt)}` }),
        el('div', {
          class: 'mono cell-muted',
          textContent: `Original transaction: ${sub.originalTransactionId}`,
        }),
      ]);
    }),
  );
}

function toggleDetail(
  row: HTMLTableRowElement,
  account: AdminAccountRow,
  chevron: HTMLElement,
): void {
  const next = row.nextElementSibling;
  if (next && next.classList.contains('row-detail')) {
    next.remove();
    row.classList.remove('row-open');
    row.setAttribute('aria-expanded', 'false');
    chevron.textContent = '▸';
    return;
  }
  row.after(detailRow(account));
  row.classList.add('row-open');
  row.setAttribute('aria-expanded', 'true');
  chevron.textContent = '▾';
}

function detailField(label: string, body: Node | string, wide = false): HTMLElement {
  return el('div', { class: wide ? 'detail-wide' : '' }, [
    el('dt', { textContent: label }),
    el('dd', {}, [body]),
  ]);
}

// A session shows its exact time; no session shows the same dash and tooltip
// as the table.
function lastSeenField(account: AdminAccountRow): HTMLElement {
  const seen = lastSeenDisplay(account.lastSeenAt);
  const hasSession = seen.text !== '—';
  return el('span', {
    textContent: hasSession ? seen.tooltip : seen.text,
    ...(hasSession ? {} : { title: seen.tooltip }),
  });
}

function yesNo(value: boolean): string {
  return value ? 'Yes' : 'No';
}

function detailRow(account: AdminAccountRow): HTMLTableRowElement {
  const idLine = el('div', { class: 'cell-line' }, [
    el('code', { class: 'mono', textContent: account.id }),
    copyButton(account.id),
  ]);

  const lastSignIn = lastSignInLabel(account.lastSignInMethod);

  const fields = el('dl', { class: 'account-detail' }, [
    detailField('Account ID', idLine, true),
    detailField('Created', exactTime(account.createdAt)),
    detailField('Last seen', lastSeenField(account)),
    detailField('Last signed in with', lastSignIn ?? '—'),
    detailField('Account sign-in', signInLabel(account.signInMethod)),
    detailField('Passkey registered', yesNo(account.hasPasskey)),
    detailField('Email verified', yesNo(account.emailVerified)),
    account.scheduledDeletionAt
      ? detailField('Deletion scheduled for', exactTime(account.scheduledDeletionAt))
      : null,
    detailField('Teams', teamsList(account.organizations), true),
    detailField('App Store subscriptions', subscriptionList(account.appleSubscriptions, 'full'), true),
  ]);

  return el('tr', { class: 'row-detail' }, [
    el('td', { colSpan: COLUMNS.length }, [el('div', { class: 'account-detail-wrap' }, [fields])]),
  ]);
}

function copyButton(text: string): HTMLButtonElement {
  const btn = el('button', { class: 'btn btn-ghost btn-sm', type: 'button' }, ['Copy']);
  const flash = (label: string): void => {
    btn.textContent = label;
    window.setTimeout(() => (btn.textContent = 'Copy'), 1500);
  };
  btn.addEventListener('click', () => {
    // navigator.clipboard is absent outside a secure context.
    const write = navigator.clipboard
      ? navigator.clipboard.writeText(text)
      : Promise.reject(new Error('Clipboard unavailable'));
    void write.then(
      () => flash('Copied'),
      () => flash('Copy failed'),
    );
  });
  return btn;
}

function pagination(
  data: { page: number; totalPages: number; total: number },
  onPage: (page: number) => void,
): HTMLElement {
  return el('div', { class: 'pagination' }, [
    el(
      'button',
      { class: 'btn btn-ghost', disabled: data.page <= 1, onClick: () => onPage(data.page - 1) },
      ['‹ Prev'],
    ),
    el('span', {
      class: 'cell-muted',
      textContent: `Page ${data.page} of ${Math.max(data.totalPages, 1)}`,
    }),
    el(
      'button',
      {
        class: 'btn btn-ghost',
        disabled: data.page >= data.totalPages,
        onClick: () => onPage(data.page + 1),
      },
      ['Next ›'],
    ),
  ]);
}
